/**
 * RestoreDialog Component
 *
 * Confirmation modal with backup details, pre-restore snapshot warning,
 * progress indicator (polling), success/failure result, and rollback info.
 *
 * Phase 3.7.4: Backup Management UI
 * @module components/backups/RestoreDialog
 */

import { useState, useEffect, useRef } from 'react';
import {
  AlertIcon,
  CheckCircleIcon,
  RefreshIcon,
  UndoIcon,
} from '../common/Icons';
import type {
  BackupHistoryItem,
  RestoreDetailResponse,
  RestoreStatus,
} from '../../types/backup';
import * as backupService from '../../services/backupService';

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

interface RestoreDialogProps {
  isOpen: boolean;
  backup: BackupHistoryItem;
  tenantId: string;
  authFetch: AuthFetch;
  onClose: () => void;
  onRestoreComplete: () => void;
}

const statusMessages: Record<RestoreStatus, string> = {
  pending: 'Preparing restore...',
  verifying: 'Verifying backup integrity...',
  restoring: 'Restoring data...',
  verifying_health: 'Verifying system health...',
  completed: 'Restore completed successfully!',
  failed: 'Restore failed.',
  rolled_back: 'Restore rolled back to previous state.',
};

const statusProgress: Record<RestoreStatus, number> = {
  pending: 10,
  verifying: 30,
  restoring: 60,
  verifying_health: 85,
  completed: 100,
  failed: 100,
  rolled_back: 100,
};

function formatSize(bytes: number | null): string {
  if (!bytes) return '\u2014';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const RestoreDialog: React.FC<RestoreDialogProps> = ({
  isOpen,
  backup,
  tenantId,
  authFetch,
  onClose,
  onRestoreComplete,
}) => {
  const [phase, setPhase] = useState<'confirm' | 'progress' | 'result'>('confirm');
  const [restoreStatus, setRestoreStatus] = useState<RestoreDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [initiating, setInitiating] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const handleInitiateRestore = async () => {
    setInitiating(true);
    setError(null);
    try {
      const result = await backupService.initiateRestore(authFetch, tenantId, backup.id);
      setPhase('progress');
      startPolling(result.restore_id);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to initiate restore');
    } finally {
      setInitiating(false);
    }
  };

  const startPolling = (restoreId: string) => {
    if (pollRef.current) clearInterval(pollRef.current);

    pollRef.current = setInterval(async () => {
      try {
        const status = await backupService.getRestoreStatus(authFetch, tenantId, restoreId);
        setRestoreStatus(status);

        if (['completed', 'failed', 'rolled_back'].includes(status.status)) {
          if (pollRef.current) clearInterval(pollRef.current);
          setPhase('result');
          if (status.status === 'completed') {
            onRestoreComplete();
          }
        }
      } catch {
        // Continue polling on transient errors
      }
    }, 2000);
  };

  const handleClose = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setPhase('confirm');
    setRestoreStatus(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const currentStatus = restoreStatus?.status || 'pending';
  const progress = statusProgress[currentStatus as RestoreStatus] || 0;

  return (
    <div className="modal-overlay" onClick={handleClose} style={{ zIndex: 1100 }}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '500px', maxHeight: '70vh', overflow: 'auto' }}
      >
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px' }}>
            <UndoIcon size={20} /> Restore from Backup
          </h2>
          <button className="btn btn-ghost" onClick={handleClose}>
            \u00d7
          </button>
        </div>

        <div className="modal-body">
          {/* Confirmation Phase */}
          {phase === 'confirm' && (
            <div>
              <div
                style={{
                  background: 'rgba(234, 179, 8, 0.1)',
                  border: '1px solid rgba(234, 179, 8, 0.3)',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#eab308',
                    fontWeight: 600,
                    marginBottom: '8px',
                  }}
                >
                  <AlertIcon size={16} /> Important
                </div>
                <p
                  style={{
                    fontSize: '13px',
                    color: 'var(--text-secondary)',
                    margin: 0,
                    lineHeight: 1.5,
                  }}
                >
                  A pre-restore safety snapshot will be created automatically before restoring.
                  This allows you to roll back if anything goes wrong.
                </p>
              </div>

              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '8px',
                  padding: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                }}
              >
                <div
                  style={{
                    fontSize: '12px',
                    color: 'var(--text-secondary)',
                    marginBottom: '8px',
                    fontWeight: 600,
                  }}
                >
                  Backup Details
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '8px',
                    fontSize: '13px',
                  }}
                >
                  <div>
                    <strong>Date:</strong>{' '}
                    {backup.started_at ? new Date(backup.started_at).toLocaleString() : '\u2014'}
                  </div>
                  <div>
                    <strong>Type:</strong> {backup.backup_type}
                  </div>
                  <div>
                    <strong>Size:</strong> {formatSize(backup.size_bytes)}
                  </div>
                  <div>
                    <strong>Destination:</strong> {backup.destination_type}
                  </div>
                </div>
              </div>

              {error && (
                <div className="alert alert-error" style={{ marginTop: '12px' }}>
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Progress Phase */}
          {phase === 'progress' && (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <RefreshIcon
                size={32}
                className="spin"
                style={{ color: 'var(--accent-primary, #7c3aed)', marginBottom: '16px' }}
              />
              <div
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  marginBottom: '8px',
                  color: 'var(--text-primary)',
                }}
              >
                {statusMessages[currentStatus as RestoreStatus] || 'Processing...'}
              </div>
              {restoreStatus?.progress_message && (
                <div
                  style={{
                    fontSize: '12px',
                    color: 'var(--text-secondary)',
                    marginBottom: '16px',
                  }}
                >
                  {restoreStatus.progress_message}
                </div>
              )}
              <div
                style={{
                  height: '6px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '3px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${progress}%`,
                    background:
                      'linear-gradient(90deg, var(--accent-primary, #7c3aed), var(--accent-secondary, #06b6d4))',
                    borderRadius: '3px',
                    transition: 'width 0.5s ease',
                  }}
                />
              </div>
            </div>
          )}

          {/* Result Phase */}
          {phase === 'result' && restoreStatus && (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              {currentStatus === 'completed' ? (
                <>
                  <CheckCircleIcon
                    size={48}
                    style={{ color: 'var(--success, #22c55e)', marginBottom: '12px' }}
                  />
                  <div
                    style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: 'var(--success, #22c55e)',
                    }}
                  >
                    Restore Successful
                  </div>
                  <p
                    style={{
                      fontSize: '13px',
                      color: 'var(--text-secondary)',
                      marginTop: '8px',
                    }}
                  >
                    Your data has been restored from the selected backup.
                  </p>
                </>
              ) : (
                <>
                  <AlertIcon
                    size={48}
                    style={{ color: 'var(--error, #ef4444)', marginBottom: '12px' }}
                  />
                  <div
                    style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: 'var(--error, #ef4444)',
                    }}
                  >
                    {currentStatus === 'rolled_back' ? 'Restore Rolled Back' : 'Restore Failed'}
                  </div>
                  {restoreStatus.error_message && (
                    <p
                      style={{
                        fontSize: '13px',
                        color: 'var(--text-secondary)',
                        marginTop: '8px',
                      }}
                    >
                      {restoreStatus.error_message}
                    </p>
                  )}
                  {currentStatus === 'rolled_back' && (
                    <p
                      style={{
                        fontSize: '12px',
                        color: 'var(--text-secondary)',
                        marginTop: '4px',
                      }}
                    >
                      The system has been rolled back to its pre-restore state.
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {phase === 'confirm' && (
            <>
              <button className="btn btn-secondary" onClick={handleClose}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleInitiateRestore}
                disabled={initiating}
              >
                {initiating ? (
                  <>
                    <RefreshIcon size={14} className="spin" /> Initiating...
                  </>
                ) : (
                  'Start Restore'
                )}
              </button>
            </>
          )}
          {phase === 'progress' && (
            <button className="btn btn-secondary" onClick={handleClose}>
              Close
            </button>
          )}
          {phase === 'result' && (
            <button className="btn btn-primary" onClick={handleClose}>
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
